import React, { ReactNode } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { DataTable } from '../../../components';
import { DataTableProps } from '../../../components/DataTable/DataTable';
import { DataTableHeader } from '../../../components/DataTable/types';
import { getRevShareMethod } from '../../../data/rev-share/helpers/transactionsUtils';
import { formatDollars, formatDateMDYY } from '../../../helpers/utilityFunctions';
import { ChargesTable } from './ChargesTable';
import { PricebookTransactions } from '../../../models/RevShareData';
import { PaidByEnum } from '../../../models/LicenseData';

// Derived from p12 typography — update if font size or line-height changes.
const SUPERTITLE_HEIGHT = '22px';
const SUBTITLE_HEIGHT = '18px';

interface PricebookRowCellProps {
  supertitle?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}
const PricebookRowCell: React.FC<PricebookRowCellProps> = ({
  supertitle,
  subtitle,
  children,
  align,
}) => (
  <Grid
    display='flex'
    flexDirection='column'
    alignItems={align === 'right' ? 'flex-end' : 'flex-start'}>
    {supertitle !== undefined && (
      <Box sx={{ minHeight: SUPERTITLE_HEIGHT, display: 'flex', alignItems: 'flex-end' }}>
        {supertitle}
      </Box>
    )}
    {children}
    {subtitle !== undefined && (
      <Box sx={{ minHeight: SUBTITLE_HEIGHT, display: 'flex', alignItems: 'flex-start' }}>
        {subtitle}
      </Box>
    )}
  </Grid>
);

export const pricebookHeaders: DataTableHeader[] = [
  {
    label: 'pricebookName',
    title: 'Pricebook',
    flex: 3,
  },
  {
    label: 'price',
    title: 'Price',
    flex: 1,
  },
  {
    label: 'chargeId',
    title: 'Charge ID',
    flex: 1.25,
  },
  {
    label: 'datePaid',
    title: 'Date',
    flex: 1,
  },
  {
    label: 'quantity',
    title: 'Qty',
    flex: 0.5,
    align: 'right',
  },
  {
    label: 'amountPaid',
    title: 'Amt Paid',
    flex: 1,
    align: 'right',
  },
  {
    label: 'revShareMethod',
    title: 'Share Method',
    flex: 1.5,
    align: 'right',
  },
  {
    label: 'revShareAmount',
    title: 'Share Amt',
    flex: 1,
    align: 'right',
  },
];

export const formatPricebooks = (pricebooks: PricebookTransactions[]) => {
  return pricebooks.map((pricebook, index) => {
    const summary = pricebook.revShareSummaries[0];
    const paidBy = pricebook.chargeInfos[0]?.paidBy ?? 'Unknown';
    const rowHasSupertitle = Boolean(summary?.effectiveTimestamp);
    const rowHasSubtitle =
      Boolean(paidBy) || Boolean(pricebook.intervalType) || Boolean(summary?.revShareDorp);

    // null reserves height without content; undefined skips the slot entirely.
    const supertitleSpacer = rowHasSupertitle ? null : undefined;
    const subtitleSpacer = rowHasSubtitle ? null : undefined;

    const pricebookNameSupertitle = summary?.effectiveTimestamp ? (
      <Typography variant='p12' color='info.main' sx={{ mb: '4px' }}>
        Effective {formatDateMDYY(summary.effectiveTimestamp)}
      </Typography>
    ) : null;
    const pricebookNameSubtitle = (
      <Typography variant='p12' color='secondary'>
        {paidBy === PaidByEnum.Org ? 'Org Pays' : 'Member Pays'}
      </Typography>
    );
    const priceSubtitle = (
      <Typography variant='p12' color='secondary'>
        {pricebook.intervalType === 'MONTH'
          ? 'monthly'
          : pricebook.intervalType === 'YEAR'
            ? 'annually'
            : pricebook.intervalType === 'WEEK'
              ? 'weekly'
              : pricebook.intervalType === 'DAY'
                ? 'daily'
                : ''}
      </Typography>
    );
    const revShareMethodSubtitle =
      summary?.revShareDorp === 'D' ? (
        <Typography variant='p12' color='secondary'>
          per license
        </Typography>
      ) : summary?.revShareDorp === 'P' ? (
        <Typography variant='p12' color='secondary'>
          of ${summary?.baseAmount ?? 0} per license
        </Typography>
      ) : null;

    return {
      id: `${pricebook.pricebookId}-${summary?.effectiveTimestamp ?? ''}-${index}`,
      pricebookData: pricebook,
      pricebookName: (
        <PricebookRowCell
          supertitle={pricebookNameSupertitle ?? supertitleSpacer}
          subtitle={pricebookNameSubtitle}>
          <Typography variant='p16Bold'>{pricebook.pricebookName}</Typography>
        </PricebookRowCell>
      ),
      price: (
        <PricebookRowCell supertitle={supertitleSpacer} subtitle={priceSubtitle}>
          <Typography variant='p16Bold'>{formatDollars((pricebook?.price ?? 0) / 100)}</Typography>
        </PricebookRowCell>
      ),
      quantity: (
        <PricebookRowCell supertitle={supertitleSpacer} subtitle={subtitleSpacer} align='right'>
          <Typography variant='p16Bold' color='secondary'>
            {pricebook.quantity}
          </Typography>
        </PricebookRowCell>
      ),
      amountPaid: (
        <PricebookRowCell supertitle={supertitleSpacer} subtitle={subtitleSpacer} align='right'>
          <Typography variant='p16Bold' color='secondary'>
            {formatDollars(pricebook.chargeInfos.reduce((sum, c) => sum + (c.amountPaid ?? 0), 0))}
          </Typography>
        </PricebookRowCell>
      ),
      revShareMethod: (
        <PricebookRowCell
          supertitle={supertitleSpacer}
          subtitle={revShareMethodSubtitle ?? subtitleSpacer}
          align='right'>
          <Typography variant='p16Bold' color='secondary'>
            {getRevShareMethod(summary)}
          </Typography>
        </PricebookRowCell>
      ),
      revShareAmount: (
        <PricebookRowCell supertitle={supertitleSpacer} subtitle={subtitleSpacer} align='right'>
          <Typography variant='p16Bold' color='secondary'>
            {formatDollars(summary?.revShareTotal ?? 0)}
          </Typography>
        </PricebookRowCell>
      ),
    };
  });
};

export const PricebookTable: React.FC<DataTableProps> = ({
  data: pricebooks,
  headers = pricebookHeaders,
  lastColumnElement,
  rowStyles,
  ...rest
}) => {
  return (
    <DataTable
      headers={headers}
      data={formatPricebooks(pricebooks as PricebookTransactions[])}
      rowStyles={rowStyles}
      lastColumnElement={lastColumnElement}
      expandedContent={row => {
        const pricebook = row.pricebookData as PricebookTransactions;
        if (!pricebook) return null;

        return (
          <ChargesTable
            data={pricebook.chargeInfos}
            headers={headers}
            rowStyles={rowStyles}
            lastColumnElement={lastColumnElement}
          />
        );
      }}
      {...rest}
    />
  );
};
